import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  Copy,
  ExternalLink,
  Loader2,
  MessageCircle,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { Selo } from '@/components/common/ListaMobile';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAtualizarCobranca, useCobranca, useReembolsarCobranca } from '@/hooks/useCobrancas';
import type { Cobranca } from '@/api/endpoints/cobrancas';
import { EstornarBaixaForm, MarcarPagaForm } from './BaixaManual';
import {
  FORMA_BAIXA_ROTULO,
  rotuloForma,
  STATUS_INFO,
  dataHora,
  descricaoDaCobranca,
  linkWhatsAppDaCobranca,
  mensagemDeErro,
  nomeDoCliente,
  reaisDeCentavos,
  srcDoQrCode,
  urlSegura,
} from './formato';

function BotaoCopiar({ texto, rotulo = 'Copiar' }: { texto: string; rotulo?: string }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };
  return (
    <Button type="button" variant="secondary" size="sm" onClick={copiar} className="shrink-0">
      {copiado ? <Check size={16} /> : <Copy size={16} />}
      {copiado ? 'Copiado' : rotulo}
    </Button>
  );
}

// Código longo (copia e cola, linha digitável, link) com botão de copiar.
function CampoCopiavel({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-text-secondary">{rotulo}</span>
        <BotaoCopiar texto={valor} />
      </div>
      <p className="break-all rounded-lg border border-border bg-bg-main px-3 py-2.5 font-mono text-xs leading-relaxed text-text-title">
        {valor}
      </p>
    </div>
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
      <span className="text-text-secondary">{rotulo}</span>
      <span className="min-w-0 text-right text-text-title">{children}</span>
    </div>
  );
}

function BlocoPagamento({ c }: { c: Cobranca }) {
  const qr = srcDoQrCode(c.pix_qrcode_base64);
  const boleto = urlSegura(c.boleto_url);
  const link = urlSegura(c.link_pagamento);

  return (
    <div className="flex flex-col gap-4">
      {link && (
        <div className="flex flex-col gap-2">
          <CampoCopiavel rotulo="Link de pagamento" valor={link} />
          <p className="text-xs text-text-secondary">
            O cliente escolhe Pix, cartão (até {c.parcelas_max ?? 12}x) ou boleto nessa página.
          </p>
          <Button asChild variant="secondary" size="sm" className="self-start">
            <a href={link} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} />
              Abrir página de pagamento
            </a>
          </Button>
        </div>
      )}
      {c.pix_copia_cola && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {qr && (
            <img
              src={qr}
              alt="QR Code do Pix"
              className="mx-auto size-40 shrink-0 rounded-xl border border-border bg-white p-2 sm:mx-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <CampoCopiavel rotulo="Pix copia e cola" valor={c.pix_copia_cola} />
            {c.expira_em && (
              <p className="mt-2 text-xs text-text-secondary">Vale até {dataHora(c.expira_em)}</p>
            )}
          </div>
        </div>
      )}
      {c.boleto_linha_digitavel && (
        <CampoCopiavel rotulo="Linha digitável do boleto" valor={c.boleto_linha_digitavel} />
      )}
      {boleto && (
        <Button asChild variant="secondary" size="sm" className="self-start">
          <a href={boleto} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={16} />
            Abrir boleto em PDF
          </a>
        </Button>
      )}
    </div>
  );
}

export function DetalheCobranca({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { data: c, isLoading, isError } = useCobranca(id);
  const atualizar = useAtualizarCobranca();
  const reembolsar = useReembolsarCobranca();
  const [confirmarReembolso, setConfirmarReembolso] = useState(false);
  const [painel, setPainel] = useState<'baixa' | 'estorno' | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const fechar = () => {
    setConfirmarReembolso(false);
    setPainel(null);
    setErro(null);
    onClose();
  };

  const verificar = () => {
    if (!c) return;
    setErro(null);
    atualizar.mutate(c.id, {
      onError: (e) => setErro(mensagemDeErro(e, 'Não deu para consultar a AbacatePay.')),
    });
  };

  const fazerReembolso = () => {
    if (!c) return;
    setErro(null);
    reembolsar.mutate(c.id, {
      onSuccess: () => setConfirmarReembolso(false),
      onError: (e) => setErro(mensagemDeErro(e, 'Não deu para reembolsar.')),
    });
  };

  const whatsapp = c ? linkWhatsAppDaCobranca(c) : null;
  const pendente = c?.status === 'PENDENTE';
  const baixaManual = c?.status === 'PAGA' && Boolean(c.baixa_manual_forma);
  const podeReembolsar = c?.status === 'PAGA' && c.metodo !== 'BOLETO' && !baixaManual;

  return (
    <Modal isOpen={id != null} onClose={fechar} title="Cobrança" size="md">
      {isLoading && (
        <div className="flex items-center justify-center p-12 text-text-secondary">
          <Loader2 className="animate-spin" size={24} />
        </div>
      )}
      {isError && <p className="p-6 text-sm text-error">Não deu para carregar esta cobrança.</p>}
      {c && (
        <div className="flex flex-col gap-5 p-6 max-md:p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-3xl font-bold tabular-nums text-text-title">
                {reaisDeCentavos(c.valor_centavos)}
              </p>
              <p className="mt-1 truncate text-sm text-text-secondary">
                {descricaoDaCobranca(c)} · {rotuloForma(c)}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <Selo tom={STATUS_INFO[c.status].tom}>{STATUS_INFO[c.status].rotulo}</Selo>
              {c.modo_teste && <Selo tom="destaque">Teste</Selo>}
            </div>
          </div>

          {c.pagamento_duplicado_em && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-error/10 px-3 py-2 text-sm text-error"
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                O cliente pagou também pela AbacatePay depois da baixa manual. Devolva um dos
                pagamentos ao cliente.
              </span>
            </div>
          )}

          {pendente && (
            <div className="flex items-center gap-2 rounded-lg bg-warning/10 px-3 py-2 text-xs text-text-body">
              <Loader2 size={14} className="shrink-0 animate-spin text-warning" />
              Esperando o pagamento. Esta tela atualiza sozinha quando cair.
            </div>
          )}

          {pendente && <BlocoPagamento c={c} />}

          <div className="rounded-xl border border-border px-4 py-2">
            <Linha rotulo="Cliente">{nomeDoCliente(c)}</Linha>
            {c.cliente_documento && <Linha rotulo="CPF/CNPJ">{c.cliente_documento}</Linha>}
            {c.cliente_telefone && <Linha rotulo="Telefone">{c.cliente_telefone}</Linha>}
            <Linha rotulo="Criada em">{dataHora(c.created_at)}</Linha>
            {c.paga_em && <Linha rotulo="Paga em">{dataHora(c.paga_em)}</Linha>}
            {c.taxa_centavos != null && c.status === 'PAGA' && !baixaManual && (
              <Linha rotulo="Taxa AbacatePay">{reaisDeCentavos(c.taxa_centavos)}</Linha>
            )}
            {baixaManual && (
              <Linha rotulo="Baixa manual">
                {FORMA_BAIXA_ROTULO[c.baixa_manual_forma!]}
                {c.baixa_manual_observacao ? ` · ${c.baixa_manual_observacao}` : ''}
              </Linha>
            )}
            {c.reembolsada_em && (
              <Linha rotulo="Reembolsada em">{dataHora(c.reembolsada_em)}</Linha>
            )}
            {c.plano_nome && c.status === 'PAGA' && (
              <Linha rotulo="Plano">
                <span className="text-success">Ativado no cadastro do produtor</span>
              </Linha>
            )}
          </div>

          {erro && <p className="text-sm text-error">{erro}</p>}

          {confirmarReembolso ? (
            <div className="rounded-xl border border-error/40 bg-error/5 p-4">
              <p className="text-sm font-medium text-text-title">
                Devolver {reaisDeCentavos(c.valor_centavos)} para o cliente?
              </p>
              <p className="mt-1 text-xs text-text-secondary">
                O dinheiro volta pela AbacatePay e não dá para desfazer. Se a cobrança renovou o
                plano, o período pago por ela é desfeito e o produtor é avisado.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={fazerReembolso}
                  disabled={reembolsar.isPending}
                >
                  {reembolsar.isPending && <Loader2 size={16} className="animate-spin" />}
                  Confirmar reembolso
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmarReembolso(false)}
                >
                  Voltar
                </Button>
              </div>
            </div>
          ) : painel === 'baixa' ? (
            <MarcarPagaForm
              c={c}
              onFeito={() => setPainel(null)}
              onVoltar={() => setPainel(null)}
            />
          ) : painel === 'estorno' ? (
            <EstornarBaixaForm
              c={c}
              onFeito={() => setPainel(null)}
              onVoltar={() => setPainel(null)}
            />
          ) : (
            <div className={cn('flex flex-wrap gap-2', 'max-md:grid max-md:grid-cols-1')}>
              {pendente && whatsapp && (
                <Button asChild>
                  <a href={whatsapp} target="_blank" rel="noopener noreferrer">
                    <MessageCircle size={16} />
                    Enviar no WhatsApp
                  </a>
                </Button>
              )}
              {pendente && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={verificar}
                  disabled={atualizar.isPending}
                >
                  <RefreshCw size={16} className={cn(atualizar.isPending && 'animate-spin')} />
                  Verificar pagamento
                </Button>
              )}
              {urlSegura(c.comprovante_url) && (
                <Button asChild variant="secondary">
                  <a href={urlSegura(c.comprovante_url)!} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={16} />
                    Ver comprovante
                  </a>
                </Button>
              )}
              {pendente && (
                <Button type="button" variant="secondary" onClick={() => setPainel('baixa')}>
                  <CheckCheck size={16} />
                  Marcar como paga
                </Button>
              )}
              {podeReembolsar && (
                <Button type="button" variant="ghost" onClick={() => setConfirmarReembolso(true)}>
                  <Undo2 size={16} />
                  Reembolsar
                </Button>
              )}
              {baixaManual && (
                <Button type="button" variant="ghost" onClick={() => setPainel('estorno')}>
                  <Undo2 size={16} />
                  Estornar baixa
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
